import PropTypes from "prop-types";
import { ReduceStore } from "flux/utils";
import Dispatcher from "Dispatcher";
import dotProp from "dot-prop-immutable";
import DefineActions from "jsmodel/DefineActions";
import { auditable } from "audit";
import HybridConfig from "lib/HybridConfig";
import typeCheckingStore from "data/util/typeCheckingStore";
import userStore from "data/userStore";

const buildBaseEntityInfo = (def) => {
    const info = {
        key: def.KEY,
        label: def.LABEL,
        tableKey: def.TABLE,
        implKey: (def.KEY || "").split("/")[0],
        associations: {
            csv: [],
            cmv: [],
            asv: [],
            amv: [],
            inv: [],
        },
        keys: def.KEYS,
        scripts: {},
    };
    [
        "logicalKey",
        "magicRootPointerKey",
        "mappingSources",
        "name",
        "orderingComponents",
        "parentPointerKey",
        "pluralName",
        "type",
        "maxDepGraphSet",
        "lookup",
    ].forEach(k => info[k] = def.INFO[k]);
    return info;
};

const addMappingSourcesSearchScripts = (info) => {
    for (const src of info.mappingSources) {
        if (src.hasOwnProperty("searchScript")) {
            // eslint-disable-next-line no-new-func
            src.searchScript = new Function("instance", src.searchScript);
            if (HybridConfig.auditScriptlets()) {
                src.searchScript = auditable("searchScript", src.searchScript);
            }
        }
    }
};

/*
 * This stuff runs after _or_munge_info! That will need to be folded into this
 * (and entityFieldMetaStore) when it's finally replaced with pure data.
 */
export const loadEntityIntoState = (state, def) => {
    const info = buildBaseEntityInfo(def);
    info.typePriority = info.type === "ROOT" ? 0 : info.type === "GLOBAL" ? 1 : 2;
    for (const key of Object.keys(def.FIELD_INFO)) {
        const f = def.FIELD_INFO[key];
        if (f.type === "CompositionField") {
            info.associations[f.multiValued ? "cmv" : "csv"].push(f.key);
        } else if (f.type === "AssociationField") {
            info.associations[f.multiValued ? "amv" : "asv"].push(f.key);
        } else if (f.type === "InverseAssociationField") {
            info.associations.inv.push(f.key);
        }
    }
    if (def.INFO.versionDate instanceof Array) {
        const ps = def.INFO.versionDate;
        info.versionDate = new Date(ps[0], ps[1] - 1, ps[2]);
    }
    if (def.INFO.scripts != null) {
        const ss = def.INFO.scripts;
        if (ss.hasOwnProperty("toString")) {
            // eslint-disable-next-line no-new-func
            info.scripts.toString = new Function("instance", ss.toString);
        }
        ["filter", "enable", "isVersion"].forEach(sn => {
            if (ss.hasOwnProperty(sn)) {
                // eslint-disable-next-line no-new-func
                info.scripts[sn] = new Function(
                    "instance",
                    "var v = " + ss[sn] + ";\nreturn v == null ? false : v;"
                );
            }
        });
        if (HybridConfig.auditScriptlets()) {
            for (const sn of Object.keys(info.scripts)) {
                info.scripts[sn] = auditable(sn, info.scripts[sn]);
            }
        }
    }
    if (info.mappingSources != null) {
        addMappingSourcesSearchScripts(info);
    }

    if (userStore.getState().isEmpty()) {
        info.canCreate = info.canWrite = info.canDelete = true;
    } else if (info.lookup && !userStore.canManageLookups(info.implKey)) {
        info.canCreate = info.canWrite = info.canDelete = false;
    } else {
        info.canCreate = userStore.canCreate(info.implKey);
        info.canWrite = userStore.canWrite(info.implKey);
        info.canDelete = userStore.canDelete(info.implKey);
    }

    state = dotProp.set(state, ["byKey", def.KEY], info);
    if (state.idxTableKey.hasOwnProperty(def.TABLE)) {
        state = dotProp.set(state, ["idxTableKey", def.TABLE], v =>
            v.concat(def.KEY));
    } else {
        state = dotProp.set(state, ["idxTableKey", def.TABLE], [def.KEY]);
    }
    return state;
};

// export const recordComparator = entityInfo => {
//     entityInfo.orderingComponents...
// };

export const entityInfoComparator = (a, b) => {
    if (a.versionDate && b.versionDate) {
        return b.versionDate - a.versionDate;
    } else if (a.versionDate) {
        return -1;
    } else if (b.versionDate) {
        return 1;
    }
    if (a.typePriority !== b.typePriority) {
        return a.typePriority - b.typePriority;
    }
    if (a.name !== b.name) {
        return a.name < b.name ? -1 : 1;
    }
    // keys MUST be distinct
    return a.key < b.key ? -1 : 1;
};

export class EntityMetaStore extends ReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return {
            byKey: {},
            idxTableKey: {},
        };
    }

    reduce(state, action) {
        if (action.type === DefineActions._UNLOAD) {
            return this.getInitialState();
        }
        if (action.type === DefineActions.ENTITY) {
            return loadEntityIntoState(state, action.def);
        }
        return state;
    }

    hasInfo(entityKeyOrKeys) {
        if (typeof entityKeyOrKeys === "string") {
            entityKeyOrKeys = [entityKeyOrKeys];
        }
        const slice = this.getState().byKey;
        return entityKeyOrKeys.every(k => slice.hasOwnProperty(k));
    }

    getInfo(entityKey) {
        const infos = this.getState().byKey;
        if (! infos.hasOwnProperty(entityKey)) {
            throw new Error(`No '${entityKey}' entity is known: ${Object.keys(infos)}`);
        }
        return infos[entityKey];
    }

    getAllInfos(optionalFilter) {
        const result = [];
        const infos = this.getState().byKey;
        if (optionalFilter == null) {
            for (const ek of Object.keys(infos)) {
                result.push(infos[ek]);
            }
        } else {
            for (const ek of Object.keys(infos)) {
                const info = infos[ek];
                if (optionalFilter(info)) {
                    result.push(info);
                }
            }
        }
        return result;
    }

    // ofEntityKey supports prefix matches (i.e., can be a version key too)
    getInfosForLogicalTable(tableKey, ofEntityKey = null) {
        const result = [];
        const s = this.getState();
        if (! s.idxTableKey.hasOwnProperty(tableKey)) {
            throw new Error(`No entities are stored in table '${tableKey}'`);
        }
        for (const k of s.idxTableKey[tableKey]) {
            const info = s.byKey[k];
            if (ofEntityKey != null) {
                if (info.key === ofEntityKey) return [info];
                if (info.key.indexOf(ofEntityKey) !== 0) continue;
            }
            result.push(info);
        }
        return result;
    }

    isRootRecord(entityKey) {
        return this.getInfo(entityKey).type === "ROOT";
    }

}

EntityMetaStore.stateTypes = {
    byKey: PropTypes.objectOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        pluralName: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired, // deprecated - use name
        logicalKey: PropTypes.string.isRequired,
        tableKey: PropTypes.string.isRequired, // deprecated - use logicalKey
        implKey: PropTypes.string.isRequired,
        magicRootPointerKey: PropTypes.string,
        parentPointerKey: PropTypes.string,
        associations: PropTypes.shape({
            csv: PropTypes.arrayOf(PropTypes.string).isRequired,
            cmv: PropTypes.arrayOf(PropTypes.string).isRequired,
            asv: PropTypes.arrayOf(PropTypes.string).isRequired,
            amv: PropTypes.arrayOf(PropTypes.string).isRequired,
            inv: PropTypes.arrayOf(PropTypes.string).isRequired,
        }).isRequired,
        scripts: PropTypes.shape({
            toString: PropTypes.func,
            filter: PropTypes.func,
            enable: PropTypes.func,
            isVersion: PropTypes.func,
        }).isRequired,
        orderingComponents: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string.isRequired,
                ascending: PropTypes.bool.isRequired,
            })
        ),
        keys: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string.isRequired,
                importance: PropTypes.number.isRequired,
                fieldKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
            })
        ),
        versionDate: PropTypes.instanceOf(Date),
    })).isRequired,
    idxTableKey: PropTypes.objectOf(PropTypes.arrayOf(
        PropTypes.string
    )).isRequired,
};

const entityMetaStore = typeCheckingStore(new EntityMetaStore());

export default entityMetaStore;



// WEBPACK FOOTER //
// ./app/data/meta/entityMetaStore.js